// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CostInfoTooltip } from '@/components/cost-info-tooltip';
import { MinuteDuration } from '@/components/minute-duration';
import { PolicyGate } from '@/components/policy-gate';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TruncatedText } from '@/components/truncated-text';
import {
  DeleteOutlineRounded,
  EditOutlined,
  HistoryRounded,
  MoreVertRounded,
} from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { deleteTimeRecord } from '@/costs/api/delete-time-record';
import { TimeRecordSortProperty } from '@/costs/api/get-time-records';
import { TimeRecord } from '@/costs/api/types';
import { TimeRecordAuditDrawer } from '@/costs/components/time-record-audit-drawer';
import { TimeRecordDialog } from '@/costs/components/time-record-dialog';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';

interface Props {
  sort: Sort<TimeRecordSortProperty>;
  onSort: (property: TimeRecordSortProperty) => void;
  timeRecords: TimeRecord[];
}

/**
 * Main table component for displaying time records with all required columns:
 * Date, Person, Project, Category, Description, Hours, Rate, Total, Actions
 */
export function TimeRecordTable(props: Props) {
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  // Create sort handler for column headers
  const createSortHandler = (column: TimeRecordSortProperty) => () => {
    props.onSort(column);
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth: '920px',
          '& .MuiTableCell-root': {
            paddingRight: 0,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sortDirection={getSortDirection('incurredOn')}
              sx={{ width: '128px' }}
            >
              <TableSortLabel
                active={isLabelActive('incurredOn')}
                direction={getLabelDirection('incurredOn')}
                onClick={createSortHandler('incurredOn')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('worker')}
              sx={{ width: '160px' }}
            >
              <TableSortLabel
                active={isLabelActive('worker')}
                direction={getLabelDirection('worker')}
                onClick={createSortHandler('worker')}
              >
                Person
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('duration')}
              sx={{ width: '70px' }}
              align="right"
            >
              <TableSortLabel
                active={isLabelActive('duration')}
                direction={getLabelDirection('duration')}
                onClick={createSortHandler('duration')}
              >
                Hours
              </TableSortLabel>
            </TableCell>
            <PolicyGate path="time.rate" action="read">
              <TableCell
                sortDirection={getSortDirection('rate')}
                sx={{ width: '80px' }}
                align="right"
              >
                <TableSortLabel
                  active={isLabelActive('rate')}
                  direction={getLabelDirection('rate')}
                  onClick={createSortHandler('rate')}
                >
                  Rate
                </TableSortLabel>
              </TableCell>
            </PolicyGate>
            <PolicyGate path="time.rate" action="read">
              <TableCell
                sortDirection={getSortDirection('amount')}
                sx={{ width: '90px' }}
                align="right"
              >
                <TableSortLabel
                  active={isLabelActive('amount')}
                  direction={getLabelDirection('amount')}
                  onClick={createSortHandler('amount')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
            </PolicyGate>
            <TableCell sx={{ width: '0px' }}></TableCell>
            <TableCell
              sortDirection={getSortDirection('project')}
              sx={{ width: '220px' }}
            >
              <TableSortLabel
                active={isLabelActive('project')}
                direction={getLabelDirection('project')}
                onClick={createSortHandler('project')}
              >
                Project
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('category')}
              sx={{ width: '180px' }}
            >
              <TableSortLabel
                active={isLabelActive('category')}
                direction={getLabelDirection('category')}
                onClick={createSortHandler('category')}
              >
                Category
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: '250px' }}>Description</TableCell>
            <TableCell align="center" sx={{ width: '112px' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.timeRecords.map((timeRecord) => (
            <TimeRecordTableRow
              key={`${timeRecord.id}-${timeRecord.lastUpdatedAt?.toISOTime() || 'new'}`}
              timeRecord={timeRecord}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface TimeRecordTableRowProps {
  timeRecord: TimeRecord;
}

/**
 * Individual row component for a time records entry in the table
 */
function TimeRecordTableRow(props: TimeRecordTableRowProps) {
  const { timeRecord } = props;
  const [menuAnchorEle, setMenuAnchorEle] = useState<null | HTMLElement>(null);
  const auditLogDrawer = useDisclosure();
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const queryClient = useQueryClient();

  const deleteTimeRecordMutation = useMutation({
    mutationFn: () => deleteTimeRecord(timeRecord.key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-records'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-record-aggregate'],
      });
      deleteDialog.close();
    },
  });

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEle(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEle(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    editDialog.open();
  };

  const handleViewAuditLog = () => {
    handleMenuClose();
    auditLogDrawer.open();
  };

  const handleDelete = () => {
    handleMenuClose();
    deleteDialog.open();
  };

  const handleConfirmDelete = () => {
    deleteTimeRecordMutation.mutate();
  };

  const workerName = `${timeRecord.worker.firstName} ${timeRecord.worker.lastName}`;

  return (
    <>
      <SupportSnackbar
        isOpen={deleteTimeRecordMutation.isError}
        onClose={deleteTimeRecordMutation.reset}
        message="An error occurred while deleting the time record."
      />
      <TimeRecordAuditDrawer
        timeRecordKey={timeRecord.key}
        isOpen={auditLogDrawer.isOpen}
        onClose={auditLogDrawer.close}
      />
      <TimeRecordDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
        initialTimeRecord={{
          id: timeRecord.id,
          key: timeRecord.key,
          totalMinutes: timeRecord.totalMinutes || 0,
          totalAmount: timeRecord.totalAmount || null,
          rate: timeRecord.rate ?? null,
          description: timeRecord.description || null,
          category: timeRecord.category,
          incurredOn: timeRecord.incurredOn,
          worker: timeRecord.worker,
          attachedProject: timeRecord.attachedProject,
        }}
        invalidationKeys={[['time-records'], ['time-record-aggregate']]}
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      >
        <DialogTitle>Delete time record</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this time record? The time record
            will be permanently removed.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            time.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={deleteDialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteTimeRecordMutation.isPending}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow hover>
        <TableCell sx={{ width: '128px' }}>
          <Typography variant="body2" noWrap>
            {timeRecord.incurredOn.toFormat('MMM d, yyyy')}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: '160px' }}>
          <TruncatedText text={workerName} variant="body2" />
        </TableCell>
        <TableCell sx={{ width: '70px' }} align="right">
          {timeRecord.totalMinutes ? (
            <MinuteDuration minutes={timeRecord.totalMinutes} variant="body2" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <PolicyGate path="time.rate" action="read">
          <TableCell sx={{ width: '80px' }} align="right">
            <Typography variant="body2" noWrap>
              {formatCurrencyValue(timeRecord.rate)}
            </Typography>
          </TableCell>
        </PolicyGate>
        <PolicyGate path="time.rate" action="read">
          <TableCell sx={{ width: '90px' }} align="right">
            <Typography variant="body2" noWrap>
              {formatCurrencyValue(timeRecord.totalAmount)}
            </Typography>
          </TableCell>
        </PolicyGate>
        <TableCell sx={{ width: '0px' }}></TableCell>
        <TableCell sx={{ width: '220px' }}>
          {timeRecord.attachedProject ? (
            <TruncatedText
              text={timeRecord.attachedProject.name}
              variant="body2"
            />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: '180px' }}>
          <TruncatedText text={timeRecord.category} variant="body2" />
        </TableCell>
        <TableCell sx={{ width: '250px' }}>
          {timeRecord.description ? (
            <TruncatedText text={timeRecord.description} variant="body2" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell align="center" sx={{ width: '112px' }}>
          <CostInfoTooltip
            createdBy={timeRecord.createdBy}
            createdAt={timeRecord.createdAt}
            lastUpdatedBy={timeRecord.lastUpdatedBy}
            lastUpdatedAt={timeRecord.lastUpdatedAt}
          />
          <IconButton size="small" onClick={handleMenuClick} sx={{ ml: 0.5 }}>
            <MoreVertRounded fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <Menu
        anchorEl={menuAnchorEle}
        open={Boolean(menuAnchorEle)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteOutlineRounded fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
